import React from "react";

import { isPlainObject } from "lodash-es";

import { BooleanEditor } from "src/cases/caseView/blocks/cellEditors/BooleanCellEditor";
import { DateEditor } from "src/cases/caseView/blocks/cellEditors/DateCellEditor";
import { EnumEditor } from "src/cases/caseView/blocks/cellEditors/EnumCellEditor";
import { JsonEditor } from "src/cases/caseView/blocks/cellEditors/JsonCellEditor";
import { TagsEditor } from "src/cases/caseView/blocks/cellEditors/TagsCellEditor";
import { TextInputEditor } from "src/cases/caseView/blocks/cellEditors/TextInputCellEditor";
import {
  CellDisplayType,
  extractCurrencyAmount,
  resolveEffectiveType,
} from "src/cases/caseView/blocks/cellTypes";

type TypedCellEditorProps = {
  value: unknown;
  displayType: CellDisplayType;
  columnKey: string;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  columnValues?: unknown[];
  hasLeftIcon?: boolean;
  floorHeight?: number;
  onContentHeightChange?: (height: number) => void;
};

export const TypedCellEditor: React.FC<TypedCellEditorProps> = ({
  value,
  displayType,
  columnKey,
  onChange,
  onBlur,
  columnValues,
  hasLeftIcon,
  floorHeight,
  onContentHeightChange,
}) => {
  const effectiveType = resolveEffectiveType(value, displayType);

  switch (effectiveType.type) {
    case "string":
    case "number":
      return (
        <TextInputEditor
          columnKey={columnKey}
          floorHeight={floorHeight}
          hasLeftIcon={hasLeftIcon}
          originalType={effectiveType.type}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          onContentHeightChange={onContentHeightChange}
        />
      );

    case "currency": {
      const handleCurrencyUpdate = (newValue: unknown) => {
        if (newValue === null) {
          if (isPlainObject(value)) {
            onChange({ ...(value as Record<string, unknown>), amount: null });
          } else {
            onChange(null);
          }
        } else if (isPlainObject(value)) {
          onChange({ ...(value as Record<string, unknown>), amount: newValue });
        } else {
          onChange(newValue);
        }
      };
      return (
        <TextInputEditor
          columnKey={columnKey}
          hasLeftIcon={hasLeftIcon}
          originalType="currency"
          value={extractCurrencyAmount(value)}
          onBlur={onBlur}
          onChange={handleCurrencyUpdate}
        />
      );
    }

    case "boolean":
      return (
        <BooleanEditor
          hasLeftIcon={hasLeftIcon}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case "date":
    case "datetime":
      return (
        <DateEditor
          dateOnly={effectiveType.type === "date"}
          hasLeftIcon={hasLeftIcon}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case "enum":
      return (
        <EnumEditor
          columnValues={columnValues}
          hasLeftIcon={hasLeftIcon}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case "tags":
      return (
        <TagsEditor
          columnValues={columnValues}
          hasLeftIcon={hasLeftIcon}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case "list":
    case "object":
      return (
        <JsonEditor
          columnKey={columnKey}
          hasLeftIcon={hasLeftIcon}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      );
  }
};
